import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "query-string";
import ReactPaginate from "react-paginate";
import { useTranslation } from "react-i18next";

const Pagination = (
  props: {
    documentsCount: number;
    limit: number;
    initialPage?: number;
    /**
     * Query-string parameter holding the page number. Defaults to `page`; a screen with
     * two independent lists on it gives each its own name, so paging one does not move
     * the other.
     */
    param?: string;
  } = {
    documentsCount: 1,
    limit: 10,
    initialPage: 1,
  },
) => {
  const { param = "page", ...rest } = props;

  /**
   * The location comes from `useLocation`, not from `useHistory().location`.
   *
   * In react-router 5.3 `useHistory` reads the history context, whose value is the history
   * object itself — a stable identity that never changes, so a component subscribed only
   * to it is never re-rendered when the URL changes. `useLocation` reads the router
   * context, which is a new value on every navigation. Reading the page number off the
   * history object works only as long as something else happens to re-render the page.
   */
  const { push } = useHistory();
  const location = useLocation();
  const query = useMemo(
    () =>
      qs.parse(location.search, { parseNumbers: true }) as Record<
        string,
        unknown
      >,
    [location.search],
  );

  const { t } = useTranslation(["common"]);

  const pages = useMemo(
    () => Math.ceil(props.documentsCount / props.limit),
    [props.documentsCount, props.limit],
  );

  /**
   * Clamped to the last page that exists: a filter can shrink the result set while the
   * URL still points at page 7, and react-paginate warns and renders nothing selected
   * when `forcePage` is out of range.
   */
  const page = useMemo(() => {
    const current = Number(query[param]) || 1;

    return Math.min(Math.max(current - 1, 0), Math.max(pages - 1, 0));
  }, [query, param, pages]);

  return (
    <ReactPaginate
      {...rest}
      previousLabel={t("common:previous")}
      nextLabel={t("common:next")}
      breakLabel="..."
      pageCount={pages}
      marginPagesDisplayed={2}
      pageRangeDisplayed={5}
      forcePage={page}
      onPageChange={(data) =>
        push({
          search: qs.stringify({ ...query, [param]: data.selected + 1 }),
        })
      }
      containerClassName="pagination"
      pageClassName="page-item"
      pageLinkClassName="page-link"
      activeClassName="active"
      activeLinkClassName="active"
      previousClassName="page-item"
      previousLinkClassName="page-link"
      nextClassName="page-item"
      nextLinkClassName="page-link"
      breakClassName="page-item"
      breakLinkClassName="page-link"
    />
  );
};

export default Pagination;
